/**
  @name StoryProductTile
  @description Alternative Product tile used in product bricks and story carousel
  @class
  @author James Norton <jamnorton@deloitte.com>
  @example  <StoryProductTile  />
*/

// THIRD-PARTY
import * as React from "react";
import classNames from "classnames";
import PropTypes from "prop-types";
import { Price, constructSizes } from "@lululemon/ui-library";
import { MAX_GRID_WIDTH } from "@lululemon/ecom-common/src/lib/breakpoints";
import SwatchCarousel from "@lululemon/ecom-common/src/components/slider/swatch-carousel";
import HoverImage from "@lululemon/ecom-common/src/components/hover-image/hover-image";
// UTILITIES
import {
  uniqueSkuStyleOrder,
  constructImageUrls,
  constructSwatches,
  insertLineBreak,
} from "@lululemon/ecom-common/src/lib/tile-utils";
import LinkGenerator from "@lululemon/ecom-common/src/lib/link-generator";
import {
  analyticsProductLinkDataAttributes,
  getProductAnalytics,
  formatRvRecosTracking,
} from "@lululemon/ecom-common/src/lib/analytics";
import "./story-product-tile.scss";
import getPriceProps from "@lululemon/ecom-common/src/util/price-utils";

const sizes = constructSizes([
  { breakpoint: MAX_GRID_WIDTH, slotSize: "312px" },
  { breakpoint: "981px", slotSize: "23vw" },
  { breakpoint: "641px", slotSize: "31vw" },
  { slotSize: "46vw" },
]);

class StoryProductTile extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      selectedSwatchIndex: 0,
    };

    this.setSelectedSwatch = this.setSelectedSwatch.bind(this);
    this.handleLinkOnFocused = this.handleLinkOnFocused.bind(this);
    this.getAnalyticsAttributes = this.getAnalyticsAttributes.bind(this);
  }

  get buildImages() {
    let initState;
    const { product } = this.props;
    if (product) {
      // remove duplicate color swatches caused by white space
      const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
      // IF WE HAVE SKU STYLES, FIND SWATCHES & HOVER IMAGES
      if (skuStyleOrder.length) {
        const images = constructImageUrls(skuStyleOrder);
        const swatches = constructSwatches(skuStyleOrder);
        initState = {
          images,
          swatches,
        };
      } else {
        initState = {
          images: [
            {
              src: product.imageUrl,
              hover: "",
            },
          ],
          swatches: [],
        };
      }
    }
    return initState;
  }

  get dataLuluTrackValue() {
    const { dataType } = this.props;
    switch (dataType.toLowerCase()) {
      case "recently viewed":
        return "recently-viewed-recommendations-product";
      case "goes well with":
        return "pdp-supplemental-recommendation";
      case "you may also like":
        return "pdp-ymal-recommendation";
      default:
        return "story-pdp-link";
    }
  }

  setSelectedSwatch(e, value, index) {
    // Need this timeout to avoid a blaring change when swatch is changed
    setTimeout(() => {
      this.setState({
        selectedSwatchIndex: index,
      });
    }, 100);

    // this is better if there are subsequent actions that need to await the setState
    /* await setAsyncTimeout(() => {
      this.setState({
        selectedSwatchIndex: index,
      });
    }, 100); */
  }

  getAnalyticsAttributes(product, skuStyle, displayType, optionalAttributes) {
    const { index, dataType } = this.props;
    let overrides = {};

    if (dataType === "goes well with") {
      overrides = {
        color: product["color-group"],
        skuID: product["default-sku"],
        version: 2.3,
      };
    } else if (dataType === "recently viewed") {
      const { skuId } = formatRvRecosTracking(product);
      overrides = {
        skuID: skuId,
        productId: product["repository-id"],
        component: "carousel",
        displayType,
        version: 2.3,
      };
    }
    const membershipAnalytics = getProductAnalytics(
      product,
      skuStyle,
      index,
      overrides
    );
    const productLinkAttributes = analyticsProductLinkDataAttributes({
      product,
      skuStyle,
    });

    return {
      "data-lulu-track": this.dataLuluTrackValue,
      "data-lulu-attributes": JSON.stringify({ ...membershipAnalytics }),
      ...productLinkAttributes,
      ...product?.additionalAnalytics,
      ...optionalAttributes,
    };
  }

  handleLinkOnFocused() {
    const { index, onFocusSlideItem } = this.props;
    if (onFocusSlideItem) {
      onFocusSlideItem(index);
    }
  }

  render() {
    const {
      analyticsComponentLinkAttr,
      headingLevel: Heading,
      isMobileDevice,
      price,
      product,
      tileType,
      onTileSelected,
      dataType,
      index,
      hideSwatches,
    } = this.props;
    const { selectedSwatchIndex } = this.state;

    const altText = `View details of ${product["display-name"]}`.replace(
      "*",
      ""
    );
    const { swatches, images } = this.buildImages;
    const selectedSwatchId = swatches.length
      ? swatches[selectedSwatchIndex].id
      : null;
    const skuStyleOrder = uniqueSkuStyleOrder(product["sku-style-order"]);
    const skuStyle =
      !!skuStyleOrder.length && skuStyleOrder[selectedSwatchIndex];
    const analyticsProductIndex = index + 1;

    const swatchAnalyticsAttrs = {
      "data-component-link": analyticsComponentLinkAttr,
    };

    const storyProductTileClasses = classNames({
      "story-product-tile": true,
      "product-brick":
        tileType === "product-brick" || tileType === "product-brick-lead-in",
      "story-carousel-container":
        tileType === "story-carousel-tile" ||
        tileType === "story-carousel-tile-left",
    });

    // get correct color-id for product links
    const productColorUrl = selectedSwatchId
      ? `${product["pdp-url"]}?color=${selectedSwatchId}`
      : product["pdp-url"];

    const showSwatches = !!swatches.length && !isMobileDevice && !hideSwatches;

    return (
      <div className={storyProductTileClasses}>
        <div
          className="image-container"
          onMouseOver={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
        >
          <LinkGenerator
            className="story-product-tile__link"
            aria-label={altText}
            href={product["pdp-url"] ? productColorUrl : "###"}
            analyticsAttrs={this.getAnalyticsAttributes(
              product,
              skuStyle,
              "tile"
            )}
            onFocus={this.handleLinkOnFocused}
            onClick={onTileSelected}
          >
            <HoverImage
              alt=""
              isMobileDevice={isMobileDevice}
              sizes={sizes}
              className="story-product-tile-image"
              src={images[selectedSwatchIndex].src}
              prefix="story_carousel"
              hoverSrc={images[selectedSwatchIndex].hover}
            />
          </LinkGenerator>
        </div>
        <div className="details">
          {showSwatches && (
            <div className="product-color-swatches">
              <div className="slider-container">
                <SwatchCarousel
                  analyticsAttrs={this.getAnalyticsAttributes(
                    product,
                    skuStyle,
                    "swatch",
                    swatchAnalyticsAttrs
                  )}
                  showBopis={false}
                  swatchList={swatches}
                  product={product}
                  pdpLink={product["pdp-url"]}
                  getSwatchIndex={this.setSelectedSwatch}
                  activeSwatch={selectedSwatchIndex}
                  selectedColor={selectedSwatchId}
                  from="StoryProductTile"
                  onFocus={this.handleLinkOnFocused}
                  dataType={dataType}
                  productIndex={analyticsProductIndex}
                />
              </div>
            </div>
          )}
          <LinkGenerator
            className="story-product-tile__link"
            href={productColorUrl}
            analyticsAttrs={this.getAnalyticsAttributes(
              product,
              skuStyle,
              "label"
            )}
            tabindex={-1}
            onClick={onTileSelected}
          >
            <Heading
              className="product-name"
              dangerouslySetInnerHTML={{
                __html: insertLineBreak(product["display-name"]),
              }}
            />
          </LinkGenerator>

          {!!price && <Price {...getPriceProps(price)} />}
        </div>
      </div>
    );
  }
}

StoryProductTile.defaultProps = {
  analyticsComponentLinkAttr: undefined,
  headingLevel: "h3",
  index: 0,
  isMobileDevice: true,
  onFocusSlideItem: undefined,
  tileType: "",
  onTileSelected: () => {},
  price: null,
  dataType: "",
  hideSwatches: false,
};

StoryProductTile.propTypes = {
  analyticsComponentLinkAttr: PropTypes.string,
  headingLevel: PropTypes.string,
  index: PropTypes.number,
  isMobileDevice: PropTypes.bool,
  onFocusSlideItem: PropTypes.func,
  price: PropTypes.shape({
    listPrice: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    salePrice: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    currencyCode: PropTypes.string,
  }),
  product: PropTypes.shape({
    "display-name": PropTypes.string,
    "pdp-url": PropTypes.string,
    "sku-style-order": PropTypes.arrayOf(
      PropTypes.shape({
        "style-id": PropTypes.string,
        "color-id": PropTypes.string,
        "color-name": PropTypes.string,
      })
    ),
  }).isRequired,
  tileType: PropTypes.string,
  onTileSelected: PropTypes.func,
  dataType: PropTypes.string,
  hideSwatches: PropTypes.bool,
};

export default StoryProductTile;
